import React, { useCallback, useEffect, useRef, useState, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { GameOverModelWrapper, LayoutWrapper, LiveWrapper, LiveWrapperFull, LiveWrapperTwoCols, PitchFxCardWrapper } from './Live.style';
import { liveViewsTypes } from '../themes/liveViewsTypes';
import Skeleton from '@mlb/react-patterns/components/Skeleton/skeleton';
import GDBREAKPOINTS, { ATBAT_MODAL_BREAKPOINT } from '@gameday/components/themes/breakpoints';
import useMedia from '@gameday/utils/hooks/useMedia';
import TeamSummaryMatchupDataContainer from '@gameday/components/TeamSummaryMatchup/TeamSummaryMatchupDataContainer';
import PitchFxOverlay from '@gameday/components/PitchFx/components/Overlay/Overlay';
import PanelWideScreen from './components/PanelWideScreen';
import PanelSingleScreen from './components/PanelSingleScreen';
import LiveTabs from './components/LiveTabs';
import { ModalTypes } from '../AtBat/atbat.style';
import useResizeObserver from '@react-hook/resize-observer';
import Overlay from '../Overlay/Overlay';
import { AtBatDataContainer } from '@gameday/components/';
import changeViewAction from '@gameday/app/lib/store/actions/views';
import { GamedayFVKey, FVFirstRunKey, TYPES } from '../FieldVision/components/auth/index';
import DiskStorage from 'disk-storage/disk-storage';
import useTracking from '@gameday/utils/hooks/useTracking';
import GameOverDataContainer from '../GameOver/containers/GameOverDataContainer';
import NoOktaOrFV from './components/NoOktaOrFV';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import useNativeLoginFunc from '@gameday/utils/hooks/useNativeLoginFunc';
import PitchFxBundle from './components/PitchFxBundle';
import { createHtmlPortalNode, InPortal, OutPortal } from 'react-reverse-portal';

const LANDSCAPE = 'landscape';
const PORTRAIT = 'portrait';

const liveTabsPortalNode = createHtmlPortalNode({
    attributes: { class: 'live-tabs-portal' }
});

// Target as ref, checkOrientation as ref.
const useOrientation = (target, checkOrientation) => {
    const [ratio, setRatio] = useState('portrait');
    const currentRatio = useRef(ratio);

    useResizeObserver(target.current, (entry) => {
        if (checkOrientation.current && entry.target) {
            const { contentRect } = entry;

            // Calculate the min height needed to respect the aspect ratio
            // to help determine coverage when in landscape
            const rat = (contentRect.width / 4 * 3) >= contentRect.height;

            // If the height is insufficient to cover, switch to PORTRAIT mode
            // to stretch vertically
            const newOrientation = !rat ? PORTRAIT : LANDSCAPE;

            // If we have an orientation change, set the state and re-render
            if (newOrientation !== currentRatio.current) {
                currentRatio.current = newOrientation;
                setRatio(newOrientation);
            }
        }
    });

    return ratio;
};

const {
    XS_MAX,
    SM_MIN,
    SM_REG_MAX,
    SM_WIDE_MIN,
    SM_MAX,
    MD_MIN,
    MD_MAX,
    LG_MIN,
    LG_MAX,
    XL_MIN,
    XL_MAX,
    XXL_MIN
} = GDBREAKPOINTS;

const { SINGLENARROW, SINGLEWIDE, FULL, TWOCOLS, SINGLE, FULLNARROW, FULLWIDE } = liveViewsTypes;
let hasLocalStorage;

const liveWrapperTypeMap = {
    [FULL]: LiveWrapperFull,
    [FULLNARROW]: LiveWrapperFull,
    [FULLWIDE]: LiveWrapperFull,
    [TWOCOLS]: LiveWrapperTwoCols,
    [SINGLENARROW]: LiveWrapper,
    [SINGLE]: LiveWrapper,
    [SINGLEWIDE]: LiveWrapper

};

export const LiveTeamSummaryProps = {
    displayRecords: true,
    hideTeamNames: false
};

const trackingConfig = ['Live', 'TeamSummaryMatchup'];
const Live = ({
    isLoading,
    numberOfPlays = 0,
    retrievedAtBatPlayIndex,
    abstractGameState,
    isLimitedExperience
}) => {
    const translate = useTranslate();
    const nativeLoginFunc = useNativeLoginFunc();
    const displayAtBat = typeof retrievedAtBatPlayIndex !== 'undefined' && retrievedAtBatPlayIndex < numberOfPlays && retrievedAtBatPlayIndex >= 0;
    /*
        Live View Types:
        SingleColumnNarrow (mobile) :  <= XS_MAX,
        SingleColumnRegular : SM_MIN - SM_REG_MAX,
        SingleColumnWide : SM_WIDE_MIN - SM_MAX,
        TwoColumn: MD_MIN - MD_MAX,
        ThreeColumnNarrow: LG_MIN - LG_MAX,
        ThreeColumnFull: XL_MIN - XL_MAX,
        ThreeColumnWide:  >= XXL_MIN
    */
    const isSingleColumnNarrow = useMedia(`(max-width: ${XS_MAX})`);
    const isSingleColumnRegular = useMedia(`(min-width: ${SM_MIN}) and (max-width: ${SM_REG_MAX})`);
    const isSingleColumnWide = useMedia(`(min-width: ${SM_WIDE_MIN}) and (max-width: ${SM_MAX})`);
    const isSingleCol = isSingleColumnNarrow || isSingleColumnRegular || isSingleColumnWide;

    const isTwoColumn = useMedia(`(min-width: ${MD_MIN}) and (max-width: ${MD_MAX})`);

    const isThreeColumnNarrow = useMedia(`(min-width: ${LG_MIN}) and (max-width: ${LG_MAX})`);
    const isThreeColumnFull = useMedia(`(min-width: ${XL_MIN}) and (max-width: ${XL_MAX})`);
    const isThreeColumnWide = useMedia(`(min-width: ${XXL_MIN})`);
    const isThreeColumns = isThreeColumnNarrow || isThreeColumnFull || isThreeColumnWide;

    const isSingleColOverlay = useMedia(`(max-width: ${ATBAT_MODAL_BREAKPOINT})`);

    let viewType = isSingleCol ? SINGLEWIDE : FULLWIDE;

    if (isSingleColumnNarrow) {
        viewType = SINGLENARROW;
    } else if (isSingleColumnRegular) {
        viewType = SINGLE;
    } else if (isSingleColumnWide) {
        viewType = SINGLEWIDE;
    } else if (isTwoColumn) {
        viewType = TWOCOLS;
    } else if (isThreeColumnNarrow) {
        viewType = FULLNARROW;
    } else if (isThreeColumnFull) {
        viewType = FULL;
    } else if (isThreeColumnWide) {
        viewType = FULLWIDE;
    }

    const [gameOverContent, setGameOverContent] = useState(null);
    const [gameOverIsOpen, setGameOverIsOpen] = useState(false);
    // Handler to trigger the transition to Wrap
    const onWrapButtonHandler = useCallback(() => {
        dispatch(changeViewAction({
            game_view: 'final'
        }));
    }, []);
    // Set up the Game Over modal if we discover the game in "final"
    useEffect(() => {
        if (abstractGameState === 'Final') {
            setGameOverContent(<GameOverDataContainer
                wrapButtonHandler={ onWrapButtonHandler }
            />);
            setGameOverIsOpen(true);
        }
    }, [abstractGameState]);

    const [isAuthOpen, setAuthOpen] = useState(false);
    const onAuthClose = useCallback(() => {
        setAuthOpen(false);
    }, [setAuthOpen]);
    const onAuthOpen = useCallback(() => {
        setAuthOpen(true);
    }, [setAuthOpen]);
    const [firstRunUser, setFirstRunUser] = useState(true);
    const dispatch = useDispatch();
    const [componentType, setComponentType] = useState(TYPES.PREVIEW);
    const Tracking = useTracking();

    const [isOpen, setOpen] = useState(false);
    const [atBatContent, setAtBatContent] = useState(null);
    const [selectedAtBatIndex, setSelectedAtBatIndex] = useState(displayAtBat ? retrievedAtBatPlayIndex : undefined);
    const [rightPanelOut, setRightPanelOut] = useState(false);

    const handleRightPanelToggle = useCallback((open) => {
        setRightPanelOut(open);
    }, [setRightPanelOut]);

    useEffect(() => {
        handleRightPanelToggle(isThreeColumns);
    }, [isThreeColumns]);

    useEffect(() => {
        const indexDefined = typeof selectedAtBatIndex !== 'undefined';
        const playIndex = selectedAtBatIndex * 1;
        setAtBatContent(indexDefined ? <AtBatDataContainer playIndex={playIndex} /> : null);
        setOpen(indexDefined);
    }, [selectedAtBatIndex]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            hasLocalStorage = (window?.localStorage);
            if (hasLocalStorage) {
                const firstrun = DiskStorage.get(GamedayFVKey, FVFirstRunKey).value;
                if (firstrun !== null) {
                    setFirstRunUser(false);
                }
            }
        }
    }, []);

    const openAtBatOverlay = useCallback((e) => {
        const playIndex = e?.currentTarget?.dataset?.atbatindex;
        setSelectedAtBatIndex(playIndex);
        Tracking.track.track_atbat_detail_pageload({ playIndex });
    }, [setSelectedAtBatIndex]);

    const onClose = useCallback(() => {
        setOpen(false);
        setSelectedAtBatIndex(undefined);
        const cfg = {
            componentName: ['AtBat', 'Play Details'],
            text: 'Close',
            activity: 'Close Button Click'
        };

        Tracking.track.track_click(cfg);
    }, [setOpen, setSelectedAtBatIndex]);

    // Handles the closing of the game over modal
    const onGameOverClose = useCallback(() => {
        setGameOverIsOpen(false);
        setGameOverContent(null);
    }, [setGameOverIsOpen, setGameOverContent]);

    const onAuthCloseWithFieldVision = useCallback(() => {
        dispatch(changeViewAction({
            fieldVisionOn: true
        }));
        /// /tracking fv
        Tracking.track.track_fieldvision_pageload();
        onAuthClose();
    }, [onAuthClose]);

    const showExplore = useCallback(() => {
        DiskStorage.put(GamedayFVKey, FVFirstRunKey, false);
        setComponentType(TYPES.EXPLORE);
        onAuthOpen();
    }, [setComponentType, onAuthOpen]);
    const LiveWrapperClass = liveWrapperTypeMap?.[viewType] ?? liveWrapperTypeMap?.[FULL];

    const liveRef = useRef(null);
    const cardRef = useRef(null);
    const checkOrientation = useRef(null);
    checkOrientation.current = (isTwoColumn || isThreeColumnNarrow);

    const checkOrientationCard = useRef(null);
    checkOrientationCard.current = (isThreeColumnFull || isThreeColumnWide);

    const orientation = useOrientation(liveRef, checkOrientation);
    const orientationCard = useOrientation(cardRef, checkOrientationCard);

    const limitedFunctionalityMessage = translate('gameday_limited_message', 'Live data is limited for this game');

    const pitchFxBundle = (
        <PitchFxBundle
            onAuthOpen={onAuthOpen}
            $type={viewType}
            firstRunUser={firstRunUser}
            showExplore={showExplore}
            viewType={viewType}
            showLimitedFunctionalityChip = {isLimitedExperience}
            limitedFunctionalityMessage = {limitedFunctionalityMessage}
            nativeLoginFunc={nativeLoginFunc}
        />
    );

    const tabsOut = useMemo(() => (
        <OutPortal node={liveTabsPortalNode} />
    ), []);

    return (
        <>
            <InPortal node={liveTabsPortalNode}>
                <LiveTabs
                    type={viewType}
                    openAtBatOverlay={ openAtBatOverlay }
                    showExplore={ showExplore }
                    onAuthOpen={ onAuthOpen }
                    firstRunUser={ firstRunUser }
                    showLimitedFunctionalityChip = {isLimitedExperience}
                />
            </InPortal>
            <LiveWrapperClass
                ref={liveRef}
                isLoading={ isLoading }
                type={viewType}
                orientation={orientation}
                className={`live-orientation-${orientation}`}
                shiftFVButton={ rightPanelOut }
            >
                {isLoading
                    ? <Skeleton width='100%' height='1000px' margin='0px' />
                    : (
                        <>
                            {(isTwoColumn || isThreeColumnNarrow) && pitchFxBundle}
                            {(isThreeColumnFull || isThreeColumnWide) && (
                                <PitchFxCardWrapper type={viewType} ref={cardRef} orientation={orientationCard} className={`live-orientation-${orientationCard}`}>
                                    {pitchFxBundle}
                                </PitchFxCardWrapper>
                            )}
                            {isThreeColumns && (
                                <PanelWideScreen
                                    openAtBatOverlay={openAtBatOverlay}
                                    handleRightPanelToggle={handleRightPanelToggle}
                                    viewType={viewType}
                                    tabs={tabsOut}
                                />
                            )}
                            {isSingleCol && (
                                <>
                                    <LayoutWrapper type={viewType}>
                                        <TeamSummaryMatchupDataContainer
                                            {...LiveTeamSummaryProps}
                                            useTri={true}
                                            $viewType={ viewType }
                                            componentName={trackingConfig}
                                        />
                                        {tabsOut}
                                    </LayoutWrapper>
                                    <PitchFxOverlay />
                                </>
                            )}
                            {isTwoColumn && (
                                <PanelSingleScreen openAtBatOverlay={openAtBatOverlay} viewType={viewType} tabs={tabsOut} />
                            )}
                            <Overlay
                                isCentered={true}
                                isOpen={isOpen}
                                onClose={onClose}
                                type={isSingleColOverlay ? ModalTypes.ATBAT_VERTICAL : ModalTypes.ATBAT}
                            >
                                {atBatContent}
                            </Overlay>
                            <Overlay
                                ContentComponent={GameOverModelWrapper}
                                isCentered={true}
                                isOpen={gameOverIsOpen}
                                onClose={onGameOverClose}
                                type={ModalTypes.GAME_OVER}
                            >
                                {gameOverContent}
                            </Overlay>
                            <NoOktaOrFV
                                isSingleCol={isSingleCol}
                                isAuthOpen={isAuthOpen}
                                componentType={componentType}
                                setComponentType={setComponentType}
                                onClose={!firstRunUser ? onAuthClose : onAuthCloseWithFieldVision}
                                setFirstRunUser={setFirstRunUser}
                            />
                        </>
                    )}
            </LiveWrapperClass>
        </>
    );
};
Live.propTypes = {
    isLoading: PropTypes.bool,
    numberOfPlays: PropTypes.number,
    retrievedAtBatPlayIndex: PropTypes.number,
    abstractGameState: PropTypes.string,
    isLimitedExperience: PropTypes.bool
};

export default memo(Live);
